import { memoize } from 'lodash-es';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';

import { Locale } from './types';

dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat);

/**
 * @todo use `dateStyle` and `timeStyle` when have sufficient support
 * https://caniuse.com/mdn-javascript_builtins_intl_datetimeformat_datetimeformat_datestyle
 * https://caniuse.com/mdn-javascript_builtins_intl_datetimeformat_datetimeformat_timestyle
 */
export const DateTimeFormat = {} as Record<
  | 'DEFAULT'
  | 'DATE_TIME'
  | 'DATE_TIME_LONG'
  | 'DATE'
  | 'DATE_LONG'
  | 'DATE_SHORT'
  | 'TIME'
  | 'TIME_LONG'
  | 'TIME_SHORT',
  Intl.DateTimeFormatOptions
>;

DateTimeFormat.DEFAULT = {};

DateTimeFormat.DATE_TIME = {
  ...DateTimeFormat.DEFAULT,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
};

DateTimeFormat.DATE_TIME_LONG = {
  ...DateTimeFormat.DATE_TIME,
  month: 'long',
};

DateTimeFormat.DATE = {
  ...DateTimeFormat.DEFAULT,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
};

DateTimeFormat.DATE_LONG = {
  ...DateTimeFormat.DATE,
  month: 'long',
};

DateTimeFormat.DATE_SHORT = {
  ...DateTimeFormat.DATE,
  month: 'short',
};

DateTimeFormat.TIME = {
  ...DateTimeFormat.DEFAULT,
  hour: 'numeric',
  minute: 'numeric',
};

export function createDateTimeFormat(locale: Locale) {
  const getFormatter = memoize(
    (options: Intl.DateTimeFormatOptions) =>
      new Intl.DateTimeFormat(locale, options),
    options => JSON.stringify(options, Object.keys(options).sort()),
  );

  function formatDateTime(
    date: Date | string,
    options: Intl.DateTimeFormatOptions = DateTimeFormat.DEFAULT,
  ) {
    return getFormatter(options).format(new Date(date));
  }

  function formatDateTimeRange(
    value1: Date | string | null | undefined,
    value2: Date | string | null | undefined,
    options: Intl.DateTimeFormatOptions = DateTimeFormat.DEFAULT,
  ) {
    return [value1, value2]
      .filter(Boolean)
      .map(value => formatDateTime(value!, options))
      .join(' - ');
  }

  /**
   * @todo reimplement in RelativeTimeFormat API
   */
  function formatRelativeTime(date: Date | string) {
    return dayjs(date).locale(locale).fromNow();
  }

  function formatDate(format: string, date: Date | string | undefined) {
    return dayjs(date).locale(locale).format(format);
  }

  return {
    formatDateTime,
    formatDateTimeRange,
    formatRelativeTime,
    /**
     * @deprecated use `formatDateTime`
     */
    formatDate,
  };
}
